import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useData } from '../../../contexts/DataContext';
import DraftEventModal from './DraftEventModal';
import './DraftEventModal.css';
import DraftItemRow from '../components/DraftItemRow';
import RecurrenceGenerator from '../components/RecurrenceGenerator';
import './ScheduleStep.css';       // calendar-strip / stats / legend styles
import './CourseReviewSection.css';

// Parse date string as local time (avoids UTC midnight → wrong day in local TZ)
const parseLocalDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const WEEKDAYS = [
  { key: 'Mon', label: 'Mon' },
  { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' },
  { key: 'Sat', label: 'Sat' },
  { key: 'Sun', label: 'Sun' },
];

const formatDateRange = (start, end) => {
  const s = parseLocalDate(start);
  const e = parseLocalDate(end);
  const fmtDate = (d) => d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  if (s && e) return `${fmtDate(s)} — ${fmtDate(e)}`;
  if (s) return `Starts ${fmtDate(s)}`;
  if (e) return `Ends ${fmtDate(e)}`;
  return '';
};

/**
 * CourseReviewSection — read-only review of all extracted course data
 * with a global "Edit" toggle and expandable accordion sections for
 * class sessions and assignments. Shown after a successful syllabus scan.
 */
export default function CourseReviewSection({
  courseData,
  setCourseData,
  onCreateCourse,
  loading,
  canCreate,
  qualityPercentage,
  startInEditMode = false,
  isManualMode = false,
  onSwitchToSyllabus,
  makeShared = false,
  setMakeShared,
}) {
  const { createCourseDraft, deleteDraftEvent, deleteDraftAssignment, updateDraftEvent, updateDraftAssignment } = useData();
  const draftEvents = useMemo(() => createCourseDraft?.draftEvents || [], [createCourseDraft?.draftEvents]);
  const draftAssignments = useMemo(() => createCourseDraft?.draftAssignments || [], [createCourseDraft?.draftAssignments]);

  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [sessionsExpanded, setSessionsExpanded] = useState(startInEditMode);
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(startInEditMode);

  // Modal state for add/edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDefaultDate, setSelectedDefaultDate] = useState(null);

  // Bulk selection state (only used in edit mode)
  const [selectedEventIds, setSelectedEventIds] = useState(() => new Set());
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState(() => new Set());

  // Bulk shift state
  const [showShiftEvents, setShowShiftEvents] = useState(false);
  const [showShiftAssignments, setShowShiftAssignments] = useState(false);
  const [eventTargetDate, setEventTargetDate] = useState('');
  const [assignmentTargetDate, setAssignmentTargetDate] = useState('');

  const eventsSelectionOn = selectedEventIds.size > 0;
  const assignmentsSelectionOn = selectedAssignmentIds.size > 0;

  const shiftDate = useCallback((raw, days) => {
    if (!raw || days === 0) return raw;
    const d = parseLocalDate(raw);
    if (!d) return raw;
    d.setDate(d.getDate() + days);
    const newDateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(raw).trim())) return newDateStr;
    const timeMatch = String(raw).trim().match(/T(.+)$/);
    return timeMatch ? `${newDateStr}T${timeMatch[1]}` : newDateStr;
  }, []);

  const getEarliestDate = useCallback((ids, items, dateExtractor) => {
    let earliest = null;
    for (const id of ids) {
      const item = items.find(i => i.id === id);
      if (!item) continue;
      const d = parseLocalDate(dateExtractor(item));
      if (d && (!earliest || d < earliest)) earliest = d;
    }
    return earliest;
  }, []);

  const earliestEventDate = useMemo(() =>
    getEarliestDate(selectedEventIds, draftEvents, (e) => e.start || e.startDatetime || e.date),
    [selectedEventIds, draftEvents, getEarliestDate]
  );
  const earliestAssignmentDate = useMemo(() =>
    getEarliestDate(selectedAssignmentIds, draftAssignments, (a) => a.dueDate || a.dueDatetime),
    [selectedAssignmentIds, draftAssignments, getEarliestDate]
  );

  const computeOffset = useCallback((earliest, targetStr) => {
    if (!earliest || !targetStr) return null;
    const target = parseLocalDate(targetStr);
    if (!target) return null;
    return Math.round((target - earliest) / 86400000);
  }, []);

  const eventOffset = useMemo(() => computeOffset(earliestEventDate, eventTargetDate), [earliestEventDate, eventTargetDate, computeOffset]);
  const assignmentOffset = useMemo(() => computeOffset(earliestAssignmentDate, assignmentTargetDate), [earliestAssignmentDate, assignmentTargetDate, computeOffset]);

  const shiftSelectedEvents = useCallback(() => {
    if (selectedEventIds.size === 0 || eventOffset === null || eventOffset === 0) return;
    for (const id of selectedEventIds) {
      const ev = draftEvents.find(e => e.id === id);
      if (!ev) continue;
      const patch = {};
      if (ev.start) patch.start = shiftDate(ev.start, eventOffset);
      if (ev.startDatetime) patch.startDatetime = shiftDate(ev.startDatetime, eventOffset);
      if (ev.endDatetime) patch.endDatetime = shiftDate(ev.endDatetime, eventOffset);
      if (ev.end) patch.end = shiftDate(ev.end, eventOffset);
      if (ev.date) patch.date = shiftDate(ev.date, eventOffset);
      updateDraftEvent(id, patch);
    }
    setSelectedEventIds(new Set());
    setShowShiftEvents(false);
    setEventTargetDate('');
  }, [selectedEventIds, draftEvents, eventOffset, shiftDate, updateDraftEvent]);

  const shiftSelectedAssignments = useCallback(() => {
    if (selectedAssignmentIds.size === 0 || assignmentOffset === null || assignmentOffset === 0) return;
    for (const id of selectedAssignmentIds) {
      const asn = draftAssignments.find(a => a.id === id);
      if (!asn) continue;
      const patch = {};
      if (asn.dueDate) patch.dueDate = shiftDate(asn.dueDate, assignmentOffset);
      if (asn.dueDatetime) patch.dueDatetime = shiftDate(asn.dueDatetime, assignmentOffset);
      updateDraftAssignment(id, patch);
    }
    setSelectedAssignmentIds(new Set());
    setShowShiftAssignments(false);
    setAssignmentTargetDate('');
  }, [selectedAssignmentIds, draftAssignments, assignmentOffset, shiftDate, updateDraftAssignment]);

  const toggleSetId = (set, id) => { const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); return next; };

  // Clear stale selections
  useEffect(() => { setSelectedEventIds(prev => new Set([...prev].filter(id => draftEvents.some(e => e.id === id)))); }, [draftEvents]);
  useEffect(() => { setSelectedAssignmentIds(prev => new Set([...prev].filter(id => draftAssignments.some(a => a.id === id)))); }, [draftAssignments]);

  // ESC clears selections
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && (selectedEventIds.size > 0 || selectedAssignmentIds.size > 0)) { setSelectedEventIds(new Set()); setSelectedAssignmentIds(new Set()); setShowShiftEvents(false); setShowShiftAssignments(false); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [selectedEventIds.size, selectedAssignmentIds.size]);

  // Clear edit state when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setSelectedEventIds(new Set());
      setSelectedAssignmentIds(new Set());
      setShowShiftEvents(false);
      setShowShiftAssignments(false);
      setEventTargetDate('');
      setAssignmentTargetDate('');
    }
  }, [isEditing]);

  const openDraftItemModal = useCallback((type, id = null, defaultDate = null) => {
    setSelectedType(type);
    setSelectedId(id);
    setSelectedDefaultDate(defaultDate);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => { setIsModalOpen(false); setSelectedType(null); setSelectedId(null); }, []);

  const deleteSelectedEvents = useCallback(async () => {
    if (selectedEventIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedEventIds.size} event(s)? This can't be undone.`)) return;
    for (const id of selectedEventIds) await deleteDraftEvent(id);
    setSelectedEventIds(new Set());
  }, [selectedEventIds, deleteDraftEvent]);

  const deleteSelectedAssignments = useCallback(async () => {
    if (selectedAssignmentIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedAssignmentIds.size} assignment(s)? This can't be undone.`)) return;
    for (const id of selectedAssignmentIds) await deleteDraftAssignment(id);
    setSelectedAssignmentIds(new Set());
  }, [selectedAssignmentIds, deleteDraftAssignment]);

  // Sorted items
  const sortedEvents = useMemo(() => (draftEvents || []).slice().sort((a, b) => {
    const ta = new Date(a.start || a.startDatetime || a.date || 0).getTime() || 0;
    const tb = new Date(b.start || b.startDatetime || b.date || 0).getTime() || 0;
    return ta - tb;
  }), [draftEvents]);

  const sortedAssignments = useMemo(() => (draftAssignments || []).slice().sort((a, b) => {
    const da = (a.dueDate || a.dueDatetime || '').toString();
    const db = (b.dueDate || b.dueDatetime || '').toString();
    return da.localeCompare(db) || (a.title || '').localeCompare(b.title || '');
  }), [draftAssignments]);

  // Select all logic
  const allEventIds = useMemo(() => sortedEvents.map(e => e.id), [sortedEvents]);
  const allAssignmentIds = useMemo(() => sortedAssignments.map(a => a.id), [sortedAssignments]);
  const allEventsSelected = allEventIds.length > 0 && selectedEventIds.size === allEventIds.length;
  const someEventsSelected = selectedEventIds.size > 0 && !allEventsSelected;
  const allAssignmentsSelected = allAssignmentIds.length > 0 && selectedAssignmentIds.size === allAssignmentIds.length;
  const someAssignmentsSelected = selectedAssignmentIds.size > 0 && !allAssignmentsSelected;
  const toggleSelectAllEvents = () => { setSelectedEventIds(allEventsSelected ? new Set() : new Set(allEventIds)); };
  const toggleSelectAllAssignments = () => { setSelectedAssignmentIds(allAssignmentsSelected ? new Set() : new Set(allAssignmentIds)); };

  // Group by date
  const groupByDate = useCallback((items, dateExtractor) => {
    const groups = [];
    let currentKey = null;
    let currentItems = [];
    for (const item of items) {
      const raw = dateExtractor(item);
      const d = parseLocalDate(raw);
      const key = d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
      if (key !== currentKey) {
        if (currentItems.length > 0) groups.push({ label: currentKey, items: currentItems });
        currentKey = key;
        currentItems = [item];
      } else currentItems.push(item);
    }
    if (currentItems.length > 0) groups.push({ label: currentKey, items: currentItems });
    return groups;
  }, []);

  const groupedEvents = useMemo(() => groupByDate(sortedEvents, (e) => e.start || e.startDatetime || e.date), [sortedEvents, groupByDate]);
  const groupedAssignments = useMemo(() => groupByDate(sortedAssignments, (a) => a.dueDate || a.dueDatetime), [sortedAssignments, groupByDate]);

  // Timeline data
  const timelineData = useMemo(() => {
    const allDates = [];
    const dayMap = {};
    for (const e of draftEvents) {
      const raw = e.start || e.startDatetime || e.date;
      if (!raw) continue;
      const d = parseLocalDate(raw);
      if (d) {
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        if (!dayMap[key]) dayMap[key] = { events: 0, assignments: 0 };
        dayMap[key].events++;
        allDates.push(d);
      }
    }
    for (const a of draftAssignments) {
      const raw = a.dueDate || a.dueDatetime;
      if (!raw) continue;
      const d = parseLocalDate(raw);
      if (d) {
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        if (!dayMap[key]) dayMap[key] = { events: 0, assignments: 0 };
        dayMap[key].assignments++;
        allDates.push(d);
      }
    }
    if (allDates.length === 0) return null;
    allDates.sort((a, b) => a - b);
    const minDate = allDates[0];
    const maxDate = allDates[allDates.length - 1];
    const fmtDate = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const dateRange = `${fmtDate(minDate)} — ${fmtDate(maxDate)}`;
    const months = [];
    let cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const endMonth = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
    while (cursor <= endMonth) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const days = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const key = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        const info = dayMap[key];
        days.push({ day, key, hasEvent: !!info?.events, hasAssignment: !!info?.assignments, eventCount: info?.events || 0, assignmentCount: info?.assignments || 0 });
      }
      months.push({ label: cursor.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(), year, month, days });
      cursor = new Date(year, month + 1, 1);
    }
    const busyDays = Object.keys(dayMap).length;
    return { months, dateRange, busyDays };
  }, [draftEvents, draftAssignments]);

  const hasItems = draftEvents.length > 0 || draftAssignments.length > 0;

  const handleInputChange = (field, value) => setCourseData(prev => ({ ...prev, [field]: value }));

  // Meeting days as an array for chip selection
  const meetingDaysArr = useMemo(() => {
    const raw = courseData?.meetingDays || '';
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(d => String(d).trim()).filter(Boolean);
    return String(raw).split(',').map(d => d.trim()).filter(Boolean);
  }, [courseData?.meetingDays]);

  const toggleMeetingDay = useCallback((key) => {
    setCourseData(prev => {
      const raw = prev?.meetingDays || '';
      const current = Array.isArray(raw)
        ? raw.map(d => String(d).trim()).filter(Boolean)
        : (raw ? String(raw).split(',').map(d => d.trim()).filter(Boolean) : []);
      const next = current.includes(key)
        ? current.filter(d => d !== key)
        : [...current, key];
      return { ...prev, meetingDays: next.join(', ') };
    });
  }, [setCourseData]);

  // ── Render ──
  return (
    <div className="course-review-section" data-tour="course-review">
      {/* ─── Section Header ─── */}
      <div className="review-header">
        <div className="review-header-left">
          <h2 className="review-title">{isManualMode ? 'Build Your Course' : 'Review Your Course'}</h2>
          <p className="review-subtitle">
            {isManualMode
              ? 'Fill in your course details and add class sessions and assignments below.'
              : (<>Everything looks good below? Click <strong>Create Course</strong> to finish.{!isEditing && ' You can also edit any details before creating.'}</>)
            }
          </p>
          {isManualMode && onSwitchToSyllabus && (
            <button type="button" className="switch-mode-link" onClick={onSwitchToSyllabus}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M12 18v-6"/><path d="M9 15l3-3 3 3"/></svg>
              Switch to Syllabus Upload
            </button>
          )}
        </div>
        <button
          type="button"
          className={`review-edit-toggle ${isEditing ? 'editing' : ''}`}
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              Done Editing
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
              Edit Details
            </>
          )}
        </button>
      </div>

      {/* ─── Course Information ─── */}
      <div className="review-card">
        <div className="review-card-header">
          <span className="review-card-icon">📚</span>
          <h3 className="review-card-title">Course Information</h3>
          {typeof qualityPercentage === 'number' && qualityPercentage > 0 && (
            <span className="review-quality-badge">
              <span className="quality-dot" />
              {qualityPercentage}% confidence
            </span>
          )}
        </div>

        {isEditing ? (
          /* ── Edit Mode: form inputs ── */
          <div className="review-form">
            <div className="review-form-group full-width">
              <label htmlFor="review-courseName">Course Name <span className="required">*</span></label>
              <input id="review-courseName" type="text" placeholder="e.g. Introduction to Psychology" value={courseData.courseName} onChange={e => handleInputChange('courseName', e.target.value)} required />
            </div>
            <div className="review-form-row">
              <div className="review-form-group">
                <label htmlFor="review-semester">Semester</label>
                <input id="review-semester" type="text" placeholder="e.g. Fall 2026" value={courseData.semester || ''} onChange={e => handleInputChange('semester', e.target.value)} />
              </div>
              <div className="review-form-group">
                <label htmlFor="review-instructor">Instructor</label>
                <input id="review-instructor" type="text" placeholder="e.g. Dr. Smith" value={courseData.instructor} onChange={e => handleInputChange('instructor', e.target.value)} />
              </div>
            </div>
            <div className="review-form-row">
              <div className="review-form-group">
                <label htmlFor="review-startDate">Start Date <span className="required">*</span></label>
                <input id="review-startDate" type="date" value={courseData.startDate} onChange={e => handleInputChange('startDate', e.target.value)} required />
              </div>
              <div className="review-form-group">
                <label htmlFor="review-endDate">End Date <span className="required">*</span></label>
                <input id="review-endDate" type="date" value={courseData.endDate} onChange={e => handleInputChange('endDate', e.target.value)} required />
              </div>
            </div>
            {/* Meeting days chips */}
            <div className="review-form-group full-width">
              <label>Meeting Days</label>
              <div className="review-day-chips">
                {WEEKDAYS.map(day => (
                  <button
                    key={day.key}
                    type="button"
                    className={`review-day-chip${meetingDaysArr.includes(day.key) ? ' review-day-chip--active' : ''}`}
                    onClick={() => toggleMeetingDay(day.key)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="review-form-group full-width">
              <label htmlFor="review-materials">Required Materials</label>
              <textarea id="review-materials" rows={2} placeholder="Textbooks, lab materials, etc." value={courseData.requiredMaterials || ''} onChange={e => handleInputChange('requiredMaterials', e.target.value)} />
            </div>
            <div className="review-form-group full-width">
              <label htmlFor="review-description">Course Description</label>
              <textarea id="review-description" rows={3} placeholder="Brief overview of the course…" value={courseData.description || ''} onChange={e => handleInputChange('description', e.target.value)} />
            </div>
          </div>
        ) : (
          /* ── Read-Only Mode: display fields ── */
          <div className="review-fields">
            <div className="review-field-row review-field-hero">
              <span className="review-field-value hero-name">{courseData.courseName || 'Untitled Course'}</span>
            </div>
            <div className="review-fields-grid">
              {courseData.semester && (
                <div className="review-field">
                  <span className="review-field-label">Semester</span>
                  <span className="review-field-value">{courseData.semester}</span>
                </div>
              )}
              {courseData.instructor && (
                <div className="review-field">
                  <span className="review-field-label">Instructor</span>
                  <span className="review-field-value">{courseData.instructor}</span>
                </div>
              )}
              {(courseData.startDate || courseData.endDate) && (
                <div className="review-field">
                  <span className="review-field-label">Date Range</span>
                  <span className="review-field-value">{formatDateRange(courseData.startDate, courseData.endDate)}</span>
                </div>
              )}
              {courseData.meetingTimes && (
                <div className="review-field">
                  <span className="review-field-label">Meeting Times</span>
                  <span className="review-field-value">{courseData.meetingTimes}</span>
                </div>
              )}
              {courseData.meetingDays && (
                <div className="review-field">
                  <span className="review-field-label">Meeting Days</span>
                  <span className="review-field-value">{courseData.meetingDays}</span>
                </div>
              )}
            </div>
            {courseData.requiredMaterials && (
              <div className="review-field full-width">
                <span className="review-field-label">Required Materials</span>
                <span className="review-field-value text-block">{courseData.requiredMaterials}</span>
              </div>
            )}
            {courseData.description && (
              <div className="review-field full-width">
                <span className="review-field-label">Course Description</span>
                <span className="review-field-value text-block">{courseData.description}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Semester Timeline ─── */}
      {hasItems && timelineData && (
        <div className="review-card timeline-card">
          <div className="cal-strip-header">
            <div className="cal-strip-header-left">
              <svg className="cal-strip-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
              <span className="cal-strip-label">Your Semester at a Glance</span>
            </div>
            <span className="cal-strip-daterange">{timelineData.dateRange}</span>
          </div>
          <div className="cal-strip-stats">
            <span className="cal-stat-pill events"><span className="cal-stat-dot event" />{draftEvents.length} class session{draftEvents.length !== 1 ? 's' : ''}</span>
            <span className="cal-stat-pill assignments"><span className="cal-stat-dot assignment" />{draftAssignments.length} assignment{draftAssignments.length !== 1 ? 's' : ''}</span>
            <span className="cal-stat-pill busy">{timelineData.busyDays} active day{timelineData.busyDays !== 1 ? 's' : ''}</span>
          </div>
          <div className="cal-strip">
            {timelineData.months.map((m) => (
              <div key={`${m.label}-${m.year}`} className="cal-month">
                <span className="cal-month-label">{m.label}</span>
                <div className="cal-days">
                  {m.days.map((d) => {
                    const hasAny = d.hasEvent || d.hasAssignment;
                    const hasBoth = d.hasEvent && d.hasAssignment;
                    let tip = '';
                    if (hasAny) {
                      const parts = [];
                      if (d.hasEvent) parts.push(`${d.eventCount} class${d.eventCount !== 1 ? 'es' : ''}`);
                      if (d.hasAssignment) parts.push(`${d.assignmentCount} assignment${d.assignmentCount !== 1 ? 's' : ''}`);
                      tip = `${m.label} ${d.day}: ${parts.join(' + ')}`;
                    }
                    return (
                      <div key={d.day} className={`cal-day${hasAny ? ' has-items' : ''}${hasBoth ? ' has-both' : ''}${d.hasEvent && !d.hasAssignment ? ' event-only' : ''}${d.hasAssignment && !d.hasEvent ? ' assignment-only' : ''}`} title={tip} aria-label={tip || `${m.label} ${d.day}`}>
                        {hasAny && <div className="cal-dots">{d.hasEvent && <span className="cal-dot event" />}{d.hasAssignment && <span className="cal-dot assignment" />}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="cal-legend">
            <span className="cal-legend-item"><span className="cal-legend-swatch event" />Classes / Events</span>
            <span className="cal-legend-item"><span className="cal-legend-swatch assignment" />Assignments Due</span>
            <span className="cal-legend-item"><span className="cal-legend-swatch both" />Both</span>
            <span className="cal-legend-item empty-key"><span className="cal-legend-swatch empty" />No activity</span>
          </div>
        </div>
      )}

      {/* ─── Class Sessions Accordion ─── */}
      <div className="review-card">
        <button type="button" className="review-accordion-header" onClick={() => setSessionsExpanded(!sessionsExpanded)} aria-expanded={sessionsExpanded}>
          <div className="accordion-header-left">
            <span className="accordion-type-dot event" />
            <h3 className="accordion-title">Class Sessions</h3>
            <span className="accordion-count">{draftEvents.length}</span>
          </div>
          <div className="accordion-header-right">
            {(isEditing || isManualMode) && (
              <button type="button" className="btn-add-item" onClick={(e) => { e.stopPropagation(); openDraftItemModal('event', null, courseData.startDate || (new Date()).toISOString().slice(0,10)); }}>+ Add</button>
            )}
            <svg className={`accordion-chevron ${sessionsExpanded ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </div>
        </button>

        {sessionsExpanded && (
          <div className="accordion-content">
            {/* Recurrence generator for bulk class session creation */}
            {(isEditing || isManualMode) && (
              <RecurrenceGenerator courseData={courseData} setCourseData={setCourseData} />
            )}

            {isEditing && eventsSelectionOn && (
              <div className="bulk-toolbar" role="toolbar" aria-label="Bulk event actions">
                <span className="bulk-count">{selectedEventIds.size} selected</span>
                <span role="button" tabIndex={0} className="bulk-btn shift" onClick={() => setShowShiftEvents(s => !s)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/><path d="M10 16l2-2 2 2"/></svg>
                  Shift Dates
                </span>
                <span role="button" tabIndex={0} className="bulk-btn danger" onClick={deleteSelectedEvents}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  Delete
                </span>
                <span role="button" tabIndex={0} className="bulk-close" onClick={() => { setSelectedEventIds(new Set()); setShowShiftEvents(false); setEventTargetDate(''); }} aria-label="Cancel selection">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                </span>
              </div>
            )}
            {isEditing && showShiftEvents && eventsSelectionOn && (
              <div className="bulk-shift-panel">
                <span className="shift-label">Move first date to</span>
                <input type="date" className="shift-date-input" value={eventTargetDate} onChange={(e) => setEventTargetDate(e.target.value)} />
                {earliestEventDate && (
                  <span className="shift-preview">
                    {eventOffset != null && eventOffset !== 0
                      ? <>{Math.abs(eventOffset)}d {eventOffset > 0 ? 'later' : 'earlier'}</>
                      : eventTargetDate ? 'Same date' : <span className="shift-current">Currently {earliestEventDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                  </span>
                )}
                <span role="button" tabIndex={0} className={`shift-apply-btn${!eventOffset || eventOffset === 0 ? ' disabled' : ''}`} onClick={() => eventOffset && eventOffset !== 0 && shiftSelectedEvents()}>Apply</span>
              </div>
            )}

            {isEditing && draftEvents.length > 0 && (
              <div className="accordion-select-all">
                <span role="checkbox" aria-checked={allEventsSelected} tabIndex={0} className={`select-dot bulk-select-all ${allEventsSelected ? 'on' : ''} ${someEventsSelected ? 'indeterminate' : ''}`} onClick={toggleSelectAllEvents} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelectAllEvents(); } }}>
                  <svg className="selectCheck" viewBox="0 0 12 12" fill="none"><path d="M2.5 6l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="select-all-label">Select all</span>
              </div>
            )}

            {draftEvents.length === 0 ? (
              <div className="accordion-empty"><p>No class sessions detected.</p></div>
            ) : (
              <div className="item-list">
                {groupedEvents.map((group) => (
                  <div key={group.label || 'undated'} className="date-group">
                    {group.label && <div className="date-group-header">{group.label}</div>}
                    {group.items.map((e) => (
                      <DraftItemRow
                        key={e.id}
                        type="event"
                        item={e}
                        isCompact
                        hideDate
                        hideIcon
                        isSelected={isEditing ? selectedEventIds.has(e.id) : false}
                        onToggleSelect={isEditing ? ((evt) => { evt.stopPropagation(); setSelectedEventIds(prev => toggleSetId(prev, e.id)); }) : undefined}
                        onEdit={(t, id) => openDraftItemModal(t, id)}
                        onDelete={(t, id) => deleteDraftEvent(id)}
                        onOpen={(t, id) => openDraftItemModal(t, id)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Assignments Accordion ─── */}
      <div className="review-card">
        <button type="button" className="review-accordion-header" onClick={() => setAssignmentsExpanded(!assignmentsExpanded)} aria-expanded={assignmentsExpanded}>
          <div className="accordion-header-left">
            <span className="accordion-type-dot assignment" />
            <h3 className="accordion-title">Assignments</h3>
            <span className="accordion-count">{draftAssignments.length}</span>
          </div>
          <div className="accordion-header-right">
            {(isEditing || isManualMode) && (
              <button type="button" className="btn-add-item" onClick={(e) => { e.stopPropagation(); openDraftItemModal('assignment', null, courseData.startDate || (new Date()).toISOString().slice(0,10)); }}>+ Add</button>
            )}
            <svg className={`accordion-chevron ${assignmentsExpanded ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </div>
        </button>

        {assignmentsExpanded && (
          <div className="accordion-content">
            {isEditing && assignmentsSelectionOn && (
              <div className="bulk-toolbar" role="toolbar" aria-label="Bulk assignment actions">
                <span className="bulk-count">{selectedAssignmentIds.size} selected</span>
                <span role="button" tabIndex={0} className="bulk-btn shift" onClick={() => setShowShiftAssignments(s => !s)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/><path d="M10 16l2-2 2 2"/></svg>
                  Shift Dates
                </span>
                <span role="button" tabIndex={0} className="bulk-btn danger" onClick={deleteSelectedAssignments}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  Delete
                </span>
                <span role="button" tabIndex={0} className="bulk-close" onClick={() => { setSelectedAssignmentIds(new Set()); setShowShiftAssignments(false); setAssignmentTargetDate(''); }} aria-label="Cancel selection">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                </span>
              </div>
            )}
            {isEditing && showShiftAssignments && assignmentsSelectionOn && (
              <div className="bulk-shift-panel">
                <span className="shift-label">Move first date to</span>
                <input type="date" className="shift-date-input" value={assignmentTargetDate} onChange={(e) => setAssignmentTargetDate(e.target.value)} />
                {earliestAssignmentDate && (
                  <span className="shift-preview">
                    {assignmentOffset != null && assignmentOffset !== 0
                      ? <>{Math.abs(assignmentOffset)}d {assignmentOffset > 0 ? 'later' : 'earlier'}</>
                      : assignmentTargetDate ? 'Same date' : <span className="shift-current">Currently {earliestAssignmentDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                  </span>
                )}
                <span role="button" tabIndex={0} className={`shift-apply-btn${!assignmentOffset || assignmentOffset === 0 ? ' disabled' : ''}`} onClick={() => assignmentOffset && assignmentOffset !== 0 && shiftSelectedAssignments()}>Apply</span>
              </div>
            )}

            {isEditing && draftAssignments.length > 0 && (
              <div className="accordion-select-all">
                <span role="checkbox" aria-checked={allAssignmentsSelected} tabIndex={0} className={`select-dot bulk-select-all ${allAssignmentsSelected ? 'on' : ''} ${someAssignmentsSelected ? 'indeterminate' : ''}`} onClick={toggleSelectAllAssignments} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelectAllAssignments(); } }}>
                  <svg className="selectCheck" viewBox="0 0 12 12" fill="none"><path d="M2.5 6l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="select-all-label">Select all</span>
              </div>
            )}

            {draftAssignments.length === 0 ? (
              <div className="accordion-empty"><p>No assignments detected.</p></div>
            ) : (
              <div className="item-list">
                {groupedAssignments.map((group) => (
                  <div key={group.label || 'undated'} className="date-group">
                    {group.label && <div className="date-group-header">{group.label}</div>}
                    {group.items.map((a) => (
                      <DraftItemRow
                        key={a.id}
                        type="assignment"
                        item={a}
                        isCompact
                        hideDate
                        hideIcon
                        isSelected={isEditing ? selectedAssignmentIds.has(a.id) : false}
                        onToggleSelect={isEditing ? ((evt) => { evt.stopPropagation(); setSelectedAssignmentIds(prev => toggleSetId(prev, a.id)); }) : undefined}
                        onEdit={(t, id) => openDraftItemModal(t, id)}
                        onDelete={(t, id) => deleteDraftAssignment(id)}
                        onOpen={(t, id) => openDraftItemModal(t, id)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Create Course CTA ─── */}
      <div className="review-cta-area">
        {setMakeShared && (
          <label className="cr-shared-toggle">
            <input
              type="checkbox"
              checked={makeShared}
              onChange={(e) => setMakeShared(e.target.checked)}
            />
            <span>Make this a shared course</span>
            <span className="cr-shared-hint">Students can join with an invite code</span>
          </label>
        )}
        <p className="review-cta-summary">
          Ready to create <strong>{courseData.courseName || 'your course'}</strong>
          {draftEvents.length > 0 && <> with <strong>{draftEvents.length}</strong> class session{draftEvents.length !== 1 ? 's' : ''}</>}
          {draftAssignments.length > 0 && <> and <strong>{draftAssignments.length}</strong> assignment{draftAssignments.length !== 1 ? 's' : ''}</>}?
        </p>
        <button
          className="review-create-btn"
          data-testid="create-course-button"
          onClick={onCreateCourse}
          disabled={!canCreate || loading}
        >
          {loading ? (
            <>
              <span className="inline-spinner" aria-hidden="true" />
              Creating…
            </>
          ) : (
            <>
              <svg className="cta-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              Create Course
            </>
          )}
        </button>
      </div>

      <DraftEventModal isOpen={isModalOpen} type={selectedType} id={selectedId} defaultDate={selectedDefaultDate} onSaved={() => {}} onClose={closeModal} />
    </div>
  );
}
